import { FC, useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import config from '@medflex/utils/config';

import { pushCustomDimensions, trackPageView } from '@medflex/utils/Tracking';
import { useUserProfile } from './hooks/useUserProfile';
import { Requests } from './modules/Requests/Requests.modules';
import { Contact, Subscription, UserProfile } from './types';
import Wrapper from './Wrapper';
import { WebsocketHubSubscription } from './modules/WebsocketHubSubscription';
import { useUserIdsOnline } from './hooks/useUserIdsOnline';
import { Snippet } from './types/snippet';
import { useSnippets } from './hooks/useSnippets';
import { useContacts } from './hooks/useContacts';

export type AppProps = {
  contacts?: Contact[];
  jwt?: string;
  userProfile?: UserProfile;
  subscription?: Subscription;
  onlineUserIds?: string[];
  renderChat?: ({ id }) => React.ReactNode;
  renderVideoMenu?: ({ id, canCall }: { id?: string; canCall: boolean }) => React.ReactNode;
  snippets?: Snippet[];
};

const App: FC<AppProps> = props => {
  const {
    contacts,
    jwt,
    userProfile,
    subscription,
    onlineUserIds,
    renderChat,
    renderVideoMenu,
    snippets,
  } = props;
  const { setUserProfile } = useUserProfile();
  const { setUserIdsOnline } = useUserIdsOnline();
  const { setSnippets } = useSnippets();
  const { setContacts } = useContacts();

  useEffect(() => {
    setUserProfile(userProfile);
  }, [setUserProfile, userProfile]);

  useEffect(() => {
    setUserIdsOnline(onlineUserIds);
  }, [setUserIdsOnline, onlineUserIds]);

  useEffect(() => {
    pushCustomDimensions({ userType: userProfile!.type, clientVersion: config.VERSION });
    trackPageView();
  }, [userProfile]);

  useEffect(() => {
    setSnippets(snippets);
  }, [setSnippets, snippets]);

  useEffect(() => {
    setContacts(contacts);
  }, [setContacts, contacts]);

  return (
    <Wrapper jwt={jwt} subscription={subscription} className="overflow-hidden">
      <WebsocketHubSubscription />
      <Switch>
        <Route path="/inbox/requests/:requestId?">
          <Requests renderChat={renderChat} renderVideoMenu={renderVideoMenu} />
        </Route>
        <Route path="*">
          <Redirect push to="/inbox/requests" />
        </Route>
      </Switch>
    </Wrapper>
  );
};

export default App;
